import { forwardRef, type MouseEvent } from 'react';
import { Check, ShoppingCart } from 'lucide-react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { canCreateOnRoute } from '@/util/permissions';
import { cn } from '@/lib/utils';

interface AddToCartButtonProps
  extends Omit<ButtonProps, 'children' | 'disabled' | 'onClick' | 'variant'> {
  /** True while the cart mutation is in flight for this surface. */
  isAdding: boolean;
  /** True when the property is already in the cart (success state). */
  isAdded: boolean;
  /** Variant used for the idle ("Add to cart") state. The added state
   *  always renders with the soft-success treatment regardless. */
  idleVariant?: ButtonProps['variant'];
  /** Receives the click event so callers inside clickable cards can
   *  `e.stopPropagation()` before triggering the add. */
  onAddToCart: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Label shown in the added state. Default: "Added to cart". The
   *  New Order drawer's compact picker rows override this to "Added". */
  addedLabel?: string;
  /** Optional override for the idle label. */
  idleLabel?: string;
}

/**
 * Shared CTA for "Add to cart" surfaces (property card, property detail,
 * property drawer, new-order drawer picker). Renders three visual states
 * — idle (cart icon + idle label), adding (spinner + idle label), added
 * (check + added label in soft-success). The added state derives from
 * cart membership (see useGetCartQuery on the call site) and persists
 * across navigations, matching standard e-commerce convention.
 *
 * Permission gate (DV-2751): renders nothing for roles the cart route
 * rejects (ProtectedRoute gates ROUTES.ORDERS_CART on the same
 * canCreateOnRoute predicate). Gated here, at the shared CTA, so no
 * current or future call site can offer an add-to-cart the role can
 * never view or check out.
 */
export const AddToCartButton = forwardRef<HTMLButtonElement, AddToCartButtonProps>(
  (
    {
      isAdding,
      isAdded,
      idleVariant = 'primaryOutline',
      onAddToCart,
      addedLabel = 'Added to Cart',
      idleLabel = 'Add to Cart',
      className,
      ...rest
    },
    ref,
  ) => {
    if (!canCreateOnRoute('Order')) return null;
    if (isAdded) {
      return (
        <Button
          ref={ref}
          type="button"
          variant="outline"
          disabled
          aria-label={addedLabel}
          className={cn(
            'border-success/30 bg-success-soft text-success-soft-foreground hover:bg-success-soft disabled:opacity-100',
            '[&_svg]:text-success',
            className,
          )}
          {...rest}
        >
          <Check className="h-4 w-4" />
          {addedLabel}
        </Button>
      );
    }
    return (
      <Button
        ref={ref}
        type="button"
        variant={idleVariant}
        disabled={isAdding}
        onClick={onAddToCart}
        className={className}
        {...rest}
      >
        {isAdding ? (
          <Spinner className="h-4 w-4" />
        ) : (
          <ShoppingCart className="h-4 w-4" />
        )}
        {idleLabel}
      </Button>
    );
  },
);

AddToCartButton.displayName = 'AddToCartButton';
